// Lightweight WhatsApp click tracking → CRM (fire-and-forget beacon).
const BASE = process.env.REACT_APP_BACKEND_URL;

// --- Temporary WhatsApp routing (admin-controlled via CRM settings) ---
let _waConfig = null;
const _waConfigListeners = [];

export async function loadWaConfig() {
  try {
    const r = await fetch(`${BASE}/api/crm/public/whatsapp-config`);
    if (r.ok) {
      _waConfig = await r.json();
      _waConfigListeners.forEach((fn) => fn(_waConfig));
    }
  } catch {
    /* routing falls back to the hardcoded number in hrefs */
  }
  return _waConfig;
}

export function onWaConfig(fn) {
  if (_waConfig) fn(_waConfig);
  _waConfigListeners.push(fn);
  return () => {
    const i = _waConfigListeners.indexOf(fn);
    if (i >= 0) _waConfigListeners.splice(i, 1);
  };
}

// Rewrites a wa.me anchor to the currently active routing number (keeps ?text=).
export function applyWaRouting(anchor) {
  try {
    const num = _waConfig && _waConfig.whatsapp_number;
    if (!num) return;
    anchor.href = anchor.href.replace(/wa\.me\/\d+/i, `wa.me/${num}`);
  } catch {
    /* keep original href */
  }
}

function sessionId() {
  let id = localStorage.getItem("tf_wa_session");
  if (!id) {
    id = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    localStorage.setItem("tf_wa_session", id);
  }
  return id;
}

// First-touch UTM capture — call once on app mount. Persists for the session.
export function captureUtms() {
  try {
    if (sessionStorage.getItem("tf_utm")) return;
    const p = new URLSearchParams(window.location.search);
    const utm = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
      if (p.get(k)) utm[k] = p.get(k).slice(0, 120);
    });
    if (Object.keys(utm).length) sessionStorage.setItem("tf_utm", JSON.stringify(utm));
  } catch {
    /* tracking must never break the site */
  }
}

// Read the session's first-touch UTM params (safe, never throws).
export function getUtms() {
  try {
    return JSON.parse(sessionStorage.getItem("tf_utm") || "{}");
  } catch {
    return {};
  }
}

const ATTR_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"];

function collectTouch() {
  const p = new URLSearchParams(window.location.search);
  const t = {};
  ATTR_KEYS.forEach((k) => { if (p.get(k)) t[k] = p.get(k).slice(0, 160); });
  return t;
}

// Capture first-touch (persist in localStorage) + latest-touch (sessionStorage)
// marketing attribution. Non-PII only; safe and idempotent. Call once on mount.
export function captureAttribution() {
  try {
    const touch = collectTouch();
    const hasParams = Object.keys(touch).length > 0;
    const landing = { landing_page: window.location.pathname, referrer: (document.referrer || "").slice(0, 300) };

    // First touch — set once, never overwritten.
    if (!localStorage.getItem("tf_first_touch")) {
      localStorage.setItem("tf_first_touch", JSON.stringify({ ...touch, ...landing, ts: new Date().toISOString() }));
    }
    // Latest touch — refresh whenever new campaign params arrive this session.
    if (hasParams || !sessionStorage.getItem("tf_last_touch")) {
      sessionStorage.setItem("tf_last_touch", JSON.stringify({ ...touch, ...landing, ts: new Date().toISOString() }));
    }
  } catch {
    /* tracking must never break the site */
  }
}

// Returns { first_touch, last_touch } for sending to the CRM/lead backend.
export function getAttribution() {
  try {
    return {
      first_touch: JSON.parse(localStorage.getItem("tf_first_touch") || "{}"),
      last_touch: JSON.parse(sessionStorage.getItem("tf_last_touch") || "{}"),
    };
  } catch {
    return { first_touch: {}, last_touch: {} };
  }
}

// Compact one-line attribution string for appending to a lead message (no PII).
export function attributionSummary(formSource) {
  try {
    const { first_touch, last_touch } = getAttribution();
    const parts = [];
    if (formSource) parts.push(`form_source=${formSource}`);
    const ft = Object.entries(first_touch).filter(([k]) => k !== "ts").map(([k, v]) => `${k}=${v}`);
    const lt = Object.entries(last_touch).filter(([k]) => k !== "ts").map(([k, v]) => `${k}=${v}`);
    if (ft.length) parts.push(`first_touch[${ft.join(", ")}]`);
    if (lt.length) parts.push(`last_touch[${lt.join(", ")}]`);
    return parts.join(" · ");
  } catch {
    return formSource ? `form_source=${formSource}` : "";
  }
}

function deviceType() {
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
  return "desktop";
}

export function trackWhatsAppClick(anchor) {
  try {
    const path = window.location.pathname;
    if (/^\/(admin|projects|crm)/.test(path)) return; // internal apps excluded
    let message = "";
    let numberUsed = "";
    try {
      const u = new URL(anchor.href);
      message = u.searchParams.get("text") || "";
      numberUsed = (u.pathname || "").replace(/\//g, "");
    } catch {
      /* malformed href — skip message */
    }
    let utm = {};
    try {
      utm = JSON.parse(sessionStorage.getItem("tf_utm") || "{}");
    } catch {
      /* ignore */
    }
    const payload = {
      source_page: path.slice(0, 300),
      page_title: (document.title || "").slice(0, 300),
      button_placement: (anchor.getAttribute("data-testid") || "wa-link").slice(0, 120),
      prefilled_message: message.slice(0, 1000),
      device_type: deviceType(),
      utm_source: utm.utm_source || null,
      utm_medium: utm.utm_medium || null,
      utm_campaign: utm.utm_campaign || null,
      session_id: sessionId(),
      whatsapp_number_used: numberUsed ? `+${numberUsed}` : null,
    };
    const url = `${BASE}/api/crm/track/whatsapp-click`;
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* tracking must never break the site */
  }
}
